// src/pages/Home.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { CERT_TYPES } from '../config/site';

const Icon = ({ d, size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

// Animated counter hook
function useCounter(target, duration = 1800) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      let start = 0;
      const step = Math.ceil(target / (duration / 16));
      const timer = setInterval(() => {
        start = Math.min(start + step, target);
        setCount(start);
        if (start >= target) clearInterval(timer);
      }, 16);
    }, { threshold: 0.4 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);
  return [count, ref];
}

// Stat card with animated number
function StatCard({ value, suffix = '', label, sublabel, color = '#4A9EFF' }) {
  const [count, ref] = useCounter(value);
  return (
    <div ref={ref} style={s.statCard}>
      <div style={{ ...s.statNum, color }}>
        {count.toLocaleString()}{suffix}
      </div>
      <div style={s.statLabel}>{label}</div>
      {sublabel && <div style={s.statSub}>{sublabel}</div>}
    </div>
  );
}

export default function Home() {
  const { t, lang } = useLang();

  const features = [
    { icon: 'M4 6h16M4 12h16M4 18h7',                                                                                                      key: 'f1', color: '#4A9EFF' },
    { icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', key: 'f2', color: '#A78BFA' },
    { icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0',                                                                                key: 'f3', color: '#34D399' },
    { icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10',                                                                                  key: 'f4', color: '#FB923C' },
    { icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z',      key: 'f5', color: '#F472B6' },
    { icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',                     key: 'f6', color: '#22D3EE' },
  ];

  const industries = [
    { key: 'i1', emoji: '💊', bg: 'linear-gradient(135deg,#1E3A6E,#0D2050)' },
    { key: 'i2', emoji: '🧪', bg: 'linear-gradient(135deg,#1E3A4E,#0D2035)' },
    { key: 'i3', emoji: '🔬', bg: 'linear-gradient(135deg,#1E3E5E,#0D2040)' },
    { key: 'i4', emoji: '✨', bg: 'linear-gradient(135deg,#3E1E5E,#251040)' },
    { key: 'i5', emoji: '🌾', bg: 'linear-gradient(135deg,#1E4E3E,#0D3028)' },
    { key: 'i6', emoji: '🌿', bg: 'linear-gradient(135deg,#2E4E1E,#1C3010)' },
  ];

  // Pricing is now per-certificate based on CERT_TYPES from site config
  const plans = CERT_TYPES;

  // Trusted logos — well-known pharma/chem companies (no images needed, styled text)
  const trustedLogos = [
    { name: 'Pfizer',    color: '#0093D0' },
    { name: 'BASF',      color: '#004A96' },
    { name: 'Merck',     color: '#009FE3' },
    { name: 'Lonza',     color: '#00A19A' },
    { name: 'Sandoz',    color: '#E4003A' },
    { name: 'Bayer',     color: '#10384F' },
    { name: 'Novartis',  color: '#EC0016' },
    { name: 'ADM',       color: '#0068B5' },
  ];

  // Testimonials
  const testimonials = lang === 'de' ? [
    { name: 'Dr. Claudia Mehren', role: 'QC-Leiterin, PharmaTech GmbH', text: 'COA Lab hat unsere Dokumentationsprozesse drastisch beschleunigt. Was früher 2 Stunden dauerte, ist jetzt in 5 Minuten erledigt. Die Compliance mit USP und ICH ist nahtlos.' },
    { name: 'Marcus Voigt', role: 'Laborleiter, ChemSolutions AG', text: 'Endlich ein Tool, das versteht, was Chemiker wirklich brauchen. Die QR-Verifizierung gibt unseren Kunden die Sicherheit, die sie erwarten.' },
    { name: 'Sarah Lindqvist', role: 'Regulatory Affairs, BioNordic', text: 'Wir exportieren in 12 Länder. COA Lab generiert konforme Zertifikate für jede Jurisdiktion in Sekunden. Absolut unverzichtbar.' },
  ] : [
    { name: 'Dr. Claudia Mehren', role: 'QC Director, PharmaTech GmbH', text: 'COA Lab dramatically accelerated our documentation workflow. What used to take 2 hours now takes 5 minutes. USP and ICH compliance is seamless.' },
    { name: 'Marcus Voigt', role: 'Lab Manager, ChemSolutions AG', text: 'Finally a tool that understands what chemists actually need. The QR verification gives our clients the confidence they expect from a top-tier supplier.' },
    { name: 'Sarah Lindqvist', role: 'Regulatory Affairs, BioNordic', text: 'We export to 12 countries. COA Lab generates compliant certificates for every jurisdiction in seconds. Absolutely indispensable to our team.' },
  ];

  // Compliance badges
  const complianceBadges = ['GMP', 'USP', 'FDA 21 CFR', 'ICH Q7', 'ISO 17025', 'EU GMP'];

  // FAQ
  const faqs = lang === 'de' ? [
    { q: 'Sind die generierten ZAs rechtlich gültig?', a: 'Ja. Die ZAs folgen internationalen Standards (USP, ICH, GMP) und enthalten alle Pflichtangaben für regulatorische Einreichungen.' },
    { q: 'Kann ich mein eigenes Logo hinzufügen?', a: 'Ja, im Professional- und Enterprise-Plan können Sie Ihr Logo, Unternehmensfarben und eigene Felder hinzufügen.' },
    { q: 'Wie funktioniert die QR-Verifizierung?', a: 'Jedes Zertifikat erhält eine eindeutige ID und einen QR-Code. Kunden scannen diesen, um die Echtheit des Dokuments zu bestätigen.' },
    { q: 'Welche Exportformate sind verfügbar?', a: 'PDF (druckfertig), Word (.docx) und JSON für API-Integration. Alle Formate sind im Professional-Plan verfügbar.' },
    { q: 'Sind meine Daten sicher?', a: 'Alle Daten werden verschlüsselt auf Google Firebase gespeichert. Zertifikate sind nur für den erstellenden Benutzer sichtbar.' },
  ] : [
    { q: 'Are the generated COAs legally valid?', a: 'Yes. COAs follow international standards (USP, ICH, GMP) and include all mandatory fields required for regulatory submissions.' },
    { q: 'Can I add my own company logo?', a: 'Yes, on Professional and Enterprise plans you can add your logo, brand colors, and custom fields to every certificate.' },
    { q: 'How does QR verification work?', a: 'Each certificate gets a unique ID and QR code. Recipients scan it to instantly confirm the document is authentic and untampered.' },
    { q: 'What export formats are available?', a: 'PDF (print-ready), Word (.docx), and JSON for API integration. All formats are available on the Professional plan.' },
    { q: 'Is my data secure?', a: 'All data is encrypted and stored on Google Firebase infrastructure. Certificates are only visible to the account that created them.' },
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div style={s.page}>
      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-track { animation: marquee 22s linear infinite; }
        .marquee-track:hover { animation-play-state: paused; }
        .faq-item { border-bottom: 1px solid rgba(255,255,255,0.07); }
        .faq-btn { width:100%; background:none; border:none; cursor:pointer; display:flex; justify-content:space-between; align-items:center; padding:20px 0; font-family:inherit; }
        .faq-btn:hover .faq-q { color:#fff; }
        .faq-q { font-size:16px; font-weight:600; color:rgba(255,255,255,0.8); text-align:left; transition:color 0.2s; }
        .faq-a { font-size:14px; color:rgba(255,255,255,0.55); line-height:1.7; padding-bottom:18px; }
        .faq-icon { font-size:20px; color:#4A9EFF; flex-shrink:0; margin-left:16px; transition:transform 0.2s; }
        .faq-icon.open { transform:rotate(45deg); }
        .testimonial-card:hover { transform:translateY(-4px); border-color:rgba(74,158,255,0.3); }
        .industry-card:hover { transform:scale(1.04); }
        .feature-card:hover { border-color:rgba(255,255,255,0.15); background:rgba(255,255,255,0.05); }
      `}</style>

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section style={s.hero}>
        <div style={s.heroGlow1} />
        <div style={s.heroGlow2} />
        <div style={s.heroContent}>
          <div style={s.heroBadge}>🧬 COA Lab</div>
          <h1 style={s.heroTitle}>
            {t('home.hero.title')}{' '}
            <span style={s.heroAccent}>{t('home.hero.titleAccent')}</span>
          </h1>
          <p style={s.heroSub}>{t('home.hero.subtitle')}</p>
          <div style={s.heroActions}>
            <Link to="/register" style={s.heroCta}>{t('home.hero.cta')}</Link>
            <Link to="/sample"   style={s.heroSecondary}>{t('home.hero.sample')}</Link>
          </div>
          <div style={s.heroMeta}>
            <span style={s.heroMetaItem}>✓ {lang === 'de' ? 'Keine Kreditkarte' : 'No credit card'}</span>
            <span style={s.heroMetaDot}>·</span>
            <span style={s.heroMetaItem}>✓ {lang === 'de' ? 'Kostenlos starten' : 'Free to start'}</span>
            <span style={s.heroMetaDot}>·</span>
            <span style={s.heroMetaItem}>✓ {lang === 'de' ? 'Sofort-Download' : 'Instant download'}</span>
          </div>
        </div>

        {/* Mock COA card */}
        <div style={s.heroCard}>
          <div style={s.cardHeader}>
            <span style={s.cardLogo}>COA<span style={{ color: '#4A9EFF', fontSize: 10 }}>Lab</span></span>
            <span style={s.cardTitle}>CERTIFICATE OF ANALYSIS</span>
          </div>
          {[
            ['Product', 'Acetylsalicylic Acid'],
            ['CAS',     '50-78-2'],
            ['Batch',   'BTH-2026-001'],
            ['Purity',  '99.7%'],
          ].map(([l, v]) => (
            <div key={l} style={s.cardRow}>
              <span style={s.cardLabel}>{l}:</span>
              <span style={s.cardVal}>{v}</span>
            </div>
          ))}
          <div style={s.cardRow}>
            <span style={s.cardLabel}>Status:</span>
            <span style={s.cardPass}>✓ PASS</span>
          </div>
          <div style={s.cardRow}>
            <span style={s.cardLabel}>Date:</span>
            <span style={s.cardVal}>Jun 17, 2026</span>
          </div>
          <div style={s.cardQRrow}>
            <div style={s.cardCompliance}>
              {['GMP','USP','ICH'].map(b => <span key={b} style={s.cardBadge}>{b}</span>)}
            </div>
            <svg width={36} height={36} viewBox="0 0 36 36" style={{ opacity: 0.7 }}>
              {[[0,0],[0,1],[0,2],[1,0],[2,0],[2,1],[2,2],[0,4],[1,4],[2,4],[4,0],[4,1],[4,2],[0,5],[1,5],[3,3],[4,4],[5,4],[3,5],[5,5]].map(([r,c],i) => (
                <rect key={i} x={c*6+1} y={r*6+1} width={5} height={5} fill="#4A9EFF" rx={0.8} />
              ))}
            </svg>
          </div>
        </div>
      </section>

      {/* ── LOGO MARQUEE ──────────────────────────────────────────────────── */}
      <div style={s.marqueeWrap}>
        <div style={s.marqueeLabel}>
          {lang === 'de' ? 'Vertraut von Unternehmen weltweit' : 'Trusted by companies worldwide'}
        </div>
        <div style={s.marqueeOuter}>
          <div style={s.marqueeFade} />
          <div style={s.marqueeFadeRight} />
          <div style={{ display: 'flex', overflow: 'hidden' }}>
            <div className="marquee-track" style={s.marqueeTrack}>
              {[...trustedLogos, ...trustedLogos].map((logo, i) => (
                <div key={i} style={s.marqueeItem}>
                  <span style={{ ...s.marqueeName, color: logo.color }}>{logo.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS ─────────────────────────────────────────────────────────── */}
      <section style={s.statsSection}>
        <div style={s.statsGrid}>
          <StatCard value={10000}  suffix="+"  label={lang === 'de' ? 'Generierte Zertifikate' : 'Certificates Generated'} sublabel={lang === 'de' ? 'Und täglich mehr' : 'And growing daily'} color="#4A9EFF" />
          <StatCard value={2500}   suffix="+"  label={lang === 'de' ? 'Zufriedene Kunden' : 'Happy Customers'} sublabel={lang === 'de' ? 'Weltweit' : 'Worldwide'} color="#A78BFA" />
          <StatCard value={50}     suffix="+"  label={lang === 'de' ? 'Unterstützte Länder' : 'Countries Supported'} sublabel={lang === 'de' ? 'Globale Compliance' : 'Global compliance'} color="#34D399" />
          <StatCard value={99}     suffix=".9%" label={lang === 'de' ? 'Verfügbarkeit' : 'Uptime'} sublabel={lang === 'de' ? 'SLA garantiert' : 'SLA guaranteed'} color="#FB923C" />
        </div>
      </section>

      {/* ── STEPS ─────────────────────────────────────────────────────────── */}
      <section style={s.stepsSection}>
        <div style={s.sectionEyebrow}>{lang === 'de' ? 'So einfach geht es' : 'How it works'}</div>
        <h2 style={s.sectionTitle}>{t('home.steps.title')}</h2>
        <div style={s.stepsGrid}>
          {[1, 2, 3].map(i => (
            <div key={i} style={s.stepCard}>
              <div style={s.stepNumBubble}>{i}</div>
              {i < 3 && <div style={s.stepConnector} />}
              <h3 style={s.stepTitle}>{t(`home.steps.s${i}t`)}</h3>
              <p style={s.stepDesc}>{t(`home.steps.s${i}d`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ──────────────────────────────────────────────────────── */}
      <section id="features" style={s.section}>
        <div style={s.sectionEyebrow}>{lang === 'de' ? 'Funktionen' : 'Features'}</div>
        <h2 style={s.sectionTitle}>{t('home.features.title')}</h2>
        <div style={s.featGrid}>
          {features.map(f => (
            <div key={f.key} className="feature-card" style={s.featCard}>
              <div style={{ ...s.featIcon, background: f.color + '18', color: f.color }}>
                <Icon d={f.icon} size={22} />
              </div>
              <h3 style={s.featTitle}>{t(`home.features.${f.key}t`)}</h3>
              <p style={s.featDesc}>{t(`home.features.${f.key}d`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── COMPLIANCE STRIP ──────────────────────────────────────────────── */}
      <div style={s.complianceStrip}>
        <span style={s.complianceLabel}>
          {lang === 'de' ? '🛡️ Zertifiziert & konform mit:' : '🛡️ Certified & compliant with:'}
        </span>
        <div style={s.complianceBadges}>
          {complianceBadges.map(b => (
            <span key={b} style={s.complianceBadge}>{b}</span>
          ))}
        </div>
      </div>

      {/* ── INDUSTRIES ────────────────────────────────────────────────────── */}
      <section id="industries" style={s.section}>
        <div style={s.sectionEyebrow}>{lang === 'de' ? 'Branchen' : 'Industries'}</div>
        <h2 style={s.sectionTitle}>{t('home.industries.title')}</h2>
        <p style={s.sectionSub}>{t('home.industries.subtitle')}</p>
        <div style={s.indGrid}>
          {industries.map(ind => (
            <div key={ind.key} className="industry-card" style={{ ...s.indCard, background: ind.bg }}>
              <span style={s.indEmoji}>{ind.emoji}</span>
              <span style={s.indLabel}>{t(`home.industries.${ind.key}`)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────────────────────── */}
      <section style={s.testimonialsSection}>
        <div style={s.sectionEyebrow}>{lang === 'de' ? 'Kundenstimmen' : 'Testimonials'}</div>
        <h2 style={s.sectionTitle}>
          {lang === 'de' ? 'Was unsere Kunden sagen' : 'What our customers say'}
        </h2>
        <div style={s.testimonialsGrid}>
          {testimonials.map((tm, i) => (
            <div key={i} className="testimonial-card" style={s.testimonialCard}>
              <div style={s.testimonialStars}>{'★★★★★'}</div>
              <p style={s.testimonialText}>"{tm.text}"</p>
              <div style={s.testimonialAuthor}>
                <div style={s.testimonialAvatar}>
                  {tm.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <div style={s.testimonialName}>{tm.name}</div>
                  <div style={s.testimonialRole}>{tm.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────────────────────── */}
      <section id="pricing" style={s.section}>
        <div style={s.sectionEyebrow}>{lang === 'de' ? 'Preise' : 'Pricing'}</div>
        <h2 style={s.sectionTitle}>{t('home.pricing.title')}</h2>
        <p style={s.sectionSub}>{t('home.pricing.subtitle')}</p>
        <div style={s.pricingGrid}>
          {plans.map(plan => (
            <div key={plan.key} style={{ ...s.planCard, ...(plan.popular ? s.planHighlight : {}) }}>
              {plan.popular && (
                <div style={s.planBadge}>{lang === 'de' ? 'Beliebt' : 'Popular'}</div>
              )}
              <div style={s.planName}>{plan.name[lang] || plan.name.en}</div>
              <div style={s.planPrice}>
                ${plan.price}
                <span style={s.planPeriod}> USD</span>
              </div>
              <p style={s.planDesc}>{plan.desc[lang] || plan.desc.en}</p>
              <ul style={s.planFeatures}>
                {(plan.features[lang] || plan.features.en).map((f, i) => (
                  <li key={i} style={s.planFeature}>
                    <span style={s.checkmark}>✓</span> {f}
                  </li>
                ))}
              </ul>
              <Link to="/register" style={plan.popular ? s.planCtaHighlight : s.planCta}>
                {lang === 'de' ? 'Jetzt erstellen' : 'Get Started'}
              </Link>
            </div>
          ))}
        </div>
        <p style={s.pricingNote}>
          {lang === 'de'
            ? 'Preis pro Zertifikat · Zahlung per Kryptowährung · Kein Abonnement'
            : 'Per certificate pricing · Crypto payment · No subscription required'}
        </p>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section style={s.faqSection}>
        <div style={s.sectionEyebrow}>FAQ</div>
        <h2 style={s.sectionTitle}>
          {lang === 'de' ? 'Häufige Fragen' : 'Frequently asked questions'}
        </h2>
        <div style={s.faqList}>
          {faqs.map((faq, i) => (
            <div key={i} className="faq-item">
              <button className="faq-btn" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <span className="faq-q">{faq.q}</span>
                <span className={`faq-icon${openFaq === i ? ' open' : ''}`}>+</span>
              </button>
              {openFaq === i && <div className="faq-a">{faq.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ─────────────────────────────────────────────────────── */}
      <section style={s.finalCta}>
        <div style={s.finalCtaGlow} />
        <h2 style={s.finalCtaTitle}>
          {lang === 'de'
            ? 'Bereit, Ihr erstes Zertifikat zu erstellen?'
            : 'Ready to generate your first certificate?'}
        </h2>
        <p style={s.finalCtaSub}>
          {lang === 'de'
            ? 'Kostenlos starten. Kein Abo, keine Kreditkarte.'
            : 'Start free. No subscription, no credit card required.'}
        </p>
        <div style={s.finalCtaActions}>
          <Link to="/register" style={s.heroCta}>
            {lang === 'de' ? 'Jetzt kostenlos starten' : 'Get started for free'}
          </Link>
          <Link to="/sample" style={s.heroSecondary}>
            {lang === 'de' ? 'Muster ansehen' : 'View sample COA'}
          </Link>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────────────── */}
      <footer style={s.footer}>
        <div style={s.footerInner}>
          <div style={s.footerBrand}>
            <span style={s.footerLogo}>COA<span style={{ color: '#4A9EFF' }}>Lab</span></span>
            <span style={s.footerTagline}>
              {lang === 'de' ? 'Professionelle ZA-Generierung' : 'Professional COA Generation'}
            </span>
          </div>
          <span style={s.footerText}>{t('home.footer.rights')}</span>
          <div style={s.footerLinks}>
            <a href="#" style={s.footerLink}>{t('home.footer.privacy')}</a>
            <a href="#" style={s.footerLink}>{t('home.footer.terms')}</a>
            <a href="#" style={s.footerLink}>{t('home.footer.contact')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

const s = {
  page: { background: '#070F2B', minHeight: '100vh', color: '#fff', paddingTop: 64, fontFamily: "'Inter', -apple-system, sans-serif" },

  // Hero
  hero:          { position: 'relative', minHeight: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 1200, margin: '0 auto', padding: '60px 24px', gap: 48, overflow: 'hidden', flexWrap: 'wrap' },
  heroGlow1:     { position: 'absolute', top: '5%', left: '15%', width: 700, height: 700, background: 'radial-gradient(circle, rgba(30,94,255,0.13) 0%, transparent 70%)', pointerEvents: 'none' },
  heroGlow2:     { position: 'absolute', bottom: '5%', right: '5%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(74,158,255,0.08) 0%, transparent 70%)', pointerEvents: 'none' },
  heroContent:   { flex: 1, minWidth: 300, maxWidth: 560, position: 'relative', zIndex: 1 },
  heroBadge:     { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(30,94,255,0.15)', border: '1px solid rgba(30,94,255,0.3)', borderRadius: 20, padding: '6px 14px', fontSize: 13, color: '#4A9EFF', marginBottom: 20 },
  heroTitle:     { fontSize: 'clamp(36px,5vw,62px)', fontWeight: 800, lineHeight: 1.08, margin: '0 0 20px', color: '#fff' },
  heroAccent:    { background: 'linear-gradient(135deg, #4A9EFF, #1E5EFF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  heroSub:       { fontSize: 18, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, marginBottom: 32 },
  heroActions:   { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 },
  heroCta:       { background: 'linear-gradient(135deg, #1E5EFF, #0A3DD4)', color: '#fff', textDecoration: 'none', padding: '14px 28px', borderRadius: 12, fontWeight: 700, fontSize: 15 },
  heroSecondary: { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', textDecoration: 'none', padding: '14px 24px', borderRadius: 12, fontWeight: 600, fontSize: 15 },
  heroMeta:      { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  heroMetaItem:  { fontSize: 13, color: 'rgba(255,255,255,0.45)' },
  heroMetaDot:   { fontSize: 13, color: 'rgba(255,255,255,0.2)' },

  // Hero card
  heroCard:      { flex: '0 0 280px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: 20, backdropFilter: 'blur(8px)', position: 'relative', zIndex: 1 },
  cardHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.08)' },
  cardLogo:      { fontSize: 14, fontWeight: 800, color: '#fff' },
  cardTitle:     { fontSize: 8, fontWeight: 700, color: 'rgba(255,255,255,0.45)', letterSpacing: 0.8 },
  cardRow:       { display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  cardLabel:     { fontSize: 11, color: 'rgba(255,255,255,0.45)' },
  cardVal:       { fontSize: 11, color: '#fff', fontWeight: 500 },
  cardPass:      { fontSize: 11, color: '#34D399', fontWeight: 700 },
  cardQRrow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' },
  cardCompliance:{ display: 'flex', gap: 4 },
  cardBadge:     { fontSize: 9, fontWeight: 700, color: '#4A9EFF', background: 'rgba(74,158,255,0.12)', padding: '2px 6px', borderRadius: 4 },

  // Marquee
  marqueeWrap:      { borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '28px 0', background: 'rgba(255,255,255,0.015)' },
  marqueeLabel:     { textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 18 },
  marqueeOuter:     { position: 'relative', overflow: 'hidden' },
  marqueeFade:      { position: 'absolute', left: 0, top: 0, bottom: 0, width: 80, background: 'linear-gradient(to right, #070F2B, transparent)', zIndex: 2, pointerEvents: 'none' },
  marqueeFadeRight: { position: 'absolute', right: 0, top: 0, bottom: 0, width: 80, background: 'linear-gradient(to left, #070F2B, transparent)', zIndex: 2, pointerEvents: 'none' },
  marqueeTrack:     { display: 'flex', gap: 0, width: 'max-content' },
  marqueeItem:      { padding: '0 40px', display: 'flex', alignItems: 'center', borderRight: '1px solid rgba(255,255,255,0.06)' },
  marqueeName:      { fontSize: 18, fontWeight: 800, letterSpacing: '-0.5px', opacity: 0.7, whiteSpace: 'nowrap' },

  // Stats
  statsSection: { maxWidth: 1200, margin: '0 auto', padding: '64px 24px' },
  statsGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 20 },
  statCard:     { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 18, padding: '28px 24px', textAlign: 'center' },
  statNum:      { fontSize: 46, fontWeight: 900, lineHeight: 1, marginBottom: 8 },
  statLabel:    { fontSize: 15, fontWeight: 600, color: 'rgba(255,255,255,0.7)' },
  statSub:      { fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 4 },

  // Shared section
  section:       { maxWidth: 1200, margin: '0 auto', padding: '80px 24px' },
  sectionEyebrow:{ textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#4A9EFF', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 },
  sectionTitle:  { fontSize: 'clamp(26px,4vw,40px)', fontWeight: 800, color: '#fff', textAlign: 'center', marginBottom: 12 },
  sectionSub:    { textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: 16, marginBottom: 48, maxWidth: 600, margin: '0 auto 48px' },

  // Steps
  stepsSection: { maxWidth: 1200, margin: '0 auto', padding: '80px 24px' },
  stepsGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px,1fr))', gap: 24, marginTop: 48, position: 'relative' },
  stepCard:     { background: 'rgba(30,94,255,0.06)', border: '1px solid rgba(30,94,255,0.18)', borderRadius: 18, padding: '32px 28px', position: 'relative' },
  stepNumBubble:{ width: 44, height: 44, borderRadius: 22, background: 'linear-gradient(135deg,#1E5EFF,#0A3DD4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color: '#fff', marginBottom: 16 },
  stepConnector:{ display: 'none' },
  stepTitle:    { fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 8 },
  stepDesc:     { fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.65 },

  // Features
  featGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 20, marginTop: 48 },
  featCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: 26, transition: 'all 0.2s' },
  featIcon: { width: 46, height: 46, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 },
  featTitle:{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 8 },
  featDesc: { fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.65 },

  // Compliance strip
  complianceStrip:  { background: 'rgba(30,94,255,0.06)', borderTop: '1px solid rgba(30,94,255,0.15)', borderBottom: '1px solid rgba(30,94,255,0.15)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, flexWrap: 'wrap' },
  complianceLabel:  { fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.6)' },
  complianceBadges: { display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' },
  complianceBadge:  { background: 'rgba(74,158,255,0.12)', border: '1px solid rgba(74,158,255,0.25)', color: '#4A9EFF', fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 20, letterSpacing: 0.5 },

  // Industries
  indGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: 16, marginTop: 40 },
  indCard: { borderRadius: 18, padding: '30px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'transform 0.2s' },
  indEmoji:{ fontSize: 34 },
  indLabel:{ fontSize: 14, fontWeight: 600, color: '#fff', textAlign: 'center' },

  // Testimonials
  testimonialsSection: { background: 'rgba(255,255,255,0.015)', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '80px 24px' },
  testimonialsGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 20, maxWidth: 1200, margin: '40px auto 0' },
  testimonialCard:     { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 28, transition: 'all 0.25s' },
  testimonialStars:    { color: '#FBBF24', fontSize: 14, marginBottom: 14, letterSpacing: 2 },
  testimonialText:     { fontSize: 15, color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, marginBottom: 20, fontStyle: 'italic' },
  testimonialAuthor:   { display: 'flex', gap: 12, alignItems: 'center' },
  testimonialAvatar:   { width: 42, height: 42, borderRadius: 21, background: 'linear-gradient(135deg,#1E5EFF,#4A9EFF)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#fff', flexShrink: 0 },
  testimonialName:     { fontSize: 14, fontWeight: 700, color: '#fff' },
  testimonialRole:     { fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 },

  // Pricing
  pricingGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px,1fr))', gap: 20, marginTop: 48, maxWidth: 900, margin: '48px auto 0' },
  planCard:    { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: '32px 26px', position: 'relative' },
  planHighlight:{ background: 'linear-gradient(135deg, rgba(30,94,255,0.14), rgba(74,158,255,0.07))', border: '1px solid rgba(30,94,255,0.4)' },
  planBadge:   { position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: '#1E5EFF', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 16px', borderRadius: 20, whiteSpace: 'nowrap' },
  planName:    { fontSize: 17, fontWeight: 700, color: 'rgba(255,255,255,0.65)', marginBottom: 8 },
  planDesc:    { fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5, marginBottom: 16 },
  planPrice:   { fontSize: 42, fontWeight: 900, color: '#fff', lineHeight: 1, marginBottom: 22 },
  planPeriod:  { fontSize: 15, fontWeight: 400, color: 'rgba(255,255,255,0.45)' },
  planFeatures:{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 10 },
  planFeature: { fontSize: 14, color: 'rgba(255,255,255,0.7)', display: 'flex', gap: 8, alignItems: 'flex-start' },
  checkmark:   { color: '#34D399', fontWeight: 700, flexShrink: 0 },
  planCta:     { display: 'block', textAlign: 'center', padding: '13px', borderRadius: 11, border: '1px solid rgba(255,255,255,0.15)', color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 600 },
  planCtaHighlight: { display: 'block', textAlign: 'center', padding: '13px', borderRadius: 11, background: 'linear-gradient(135deg, #1E5EFF, #0A3DD4)', color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 700 },
  pricingNote: { textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.35)', marginTop: 24 },

  // FAQ
  faqSection: { maxWidth: 720, margin: '0 auto', padding: '80px 24px' },
  faqList:    { marginTop: 40 },

  // Final CTA
  finalCta:        { position: 'relative', textAlign: 'center', padding: '100px 24px', overflow: 'hidden', background: 'linear-gradient(180deg, transparent, rgba(30,94,255,0.06))' },
  finalCtaGlow:    { position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 400, background: 'radial-gradient(ellipse, rgba(30,94,255,0.15) 0%, transparent 70%)', pointerEvents: 'none' },
  finalCtaTitle:   { fontSize: 'clamp(28px,4vw,44px)', fontWeight: 800, color: '#fff', marginBottom: 14, position: 'relative' },
  finalCtaSub:     { fontSize: 16, color: 'rgba(255,255,255,0.55)', marginBottom: 36, position: 'relative' },
  finalCtaActions: { display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', position: 'relative' },

  // Footer
  footer:      { borderTop: '1px solid rgba(255,255,255,0.06)' },
  footerInner: { maxWidth: 1200, margin: '0 auto', padding: '28px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 },
  footerBrand: { display: 'flex', flexDirection: 'column', gap: 3 },
  footerLogo:  { fontSize: 18, fontWeight: 800, color: '#fff' },
  footerTagline:{ fontSize: 11, color: 'rgba(255,255,255,0.3)' },
  footerText:  { fontSize: 13, color: 'rgba(255,255,255,0.35)' },
  footerLinks: { display: 'flex', gap: 20 },
  footerLink:  { color: 'rgba(255,255,255,0.4)', textDecoration: 'none', fontSize: 13 },
};
